import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, Modal, Progress, Typography, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { uploadVideoAsset } from '../api'

const ACCEPTED_MIME = new Set(['video/mp4', 'video/quicktime', 'video/webm'])
const ACCEPTED_EXT = ['.mp4', '.mov', '.webm']

function isAcceptedFile(file: File): boolean {
  if (ACCEPTED_MIME.has(file.type)) return true
  const lower = file.name.toLowerCase()
  return ACCEPTED_EXT.some((ext) => lower.endsWith(ext))
}

/** Lightweight, video-only upload widget — VISTOERA_VIDEO_ASSETS_V1 §十四.
 * Deliberately NOT `components/UploadModal.tsx` (that widget is wired to
 * the legacy image AssetChain initiate/PUT-to-R2/complete presign flow —
 * see this task's audit note); this proxies the raw file straight through
 * this backend to `POST .../video-assets/upload` (browser -> backend -> R2,
 * same transport shape as `saas_video_generation.upload_asset_direct`),
 * so it never needs a second presign contract. */
export default function VideoUploadModal({
  open,
  workspaceId,
  onClose,
  onUploaded,
}: {
  open: boolean
  workspaceId: string
  onClose: () => void
  onUploaded: () => void
}) {
  const { t } = useTranslation(['video', 'actions'])
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setFile(null)
      setTitle('')
      setUploading(false)
      setProgress(0)
      setError(null)
    }
  }, [open])

  const pickFile = (f: File | null) => {
    if (!f) return
    if (!isAcceptedFile(f)) {
      setError(t('video:assets.uploadModal.unsupportedFormat'))
      return
    }
    setError(null)
    setFile(f)
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''))
  }

  const onSubmit = async () => {
    if (!file) return
    setUploading(true)
    setError(null)
    setProgress(0)
    try {
      await uploadVideoAsset(workspaceId, file, title.trim() || undefined, setProgress)
      message.success(t('video:assets.uploadModal.success'))
      onUploaded()
    } catch (e) {
      const msg =
        (e as { response?: { data?: { detail?: { message?: string } | string } } })?.response?.data?.detail
      const text = typeof msg === 'string' ? msg : msg?.message
      setError(text || (e as Error).message || t('video:assets.uploadModal.uploadFailed'))
      setUploading(false)
    }
  }

  return (
    <Modal
      open={open}
      title={t('video:assets.uploadModal.title')}
      onCancel={uploading ? undefined : onClose}
      footer={null}
      destroyOnClose
      centered
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          pickFile(e.dataTransfer.files?.[0] || null)
        }}
        style={{
          border: `1px dashed ${dragOver ? 'var(--primary)' : 'var(--border-default)'}`,
          borderRadius: 8,
          padding: '28px 16px',
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          background: dragOver ? 'var(--primary-soft)' : 'transparent',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
          style={{ display: 'none' }}
          disabled={uploading}
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
        />
        <InboxOutlined style={{ fontSize: 32, color: 'var(--text-muted)' }} />
        <div style={{ marginTop: 8 }}>
          {file ? (
            <Typography.Text strong>{file.name}</Typography.Text>
          ) : (
            <>
              <div>{t('video:assets.uploadModal.dropHint')}</div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('video:assets.uploadModal.formatHint')}
              </Typography.Text>
            </>
          )}
        </div>
      </div>

      {file && (
        <Input
          style={{ marginTop: 16 }}
          placeholder={t('video:assets.uploadModal.titlePlaceholder')}
          value={title}
          maxLength={500}
          onChange={(e) => setTitle(e.target.value)}
          disabled={uploading}
        />
      )}

      {uploading && <Progress style={{ marginTop: 16 }} percent={progress} status="active" />}
      {error && (
        <Typography.Text type="danger" style={{ display: 'block', marginTop: 12 }}>
          {error}
        </Typography.Text>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <Button onClick={onClose} disabled={uploading}>
          {t('actions:cancel')}
        </Button>
        <Button type="primary" disabled={!file || uploading} loading={uploading} onClick={() => void onSubmit()}>
          {t('video:assets.uploadModal.upload')}
        </Button>
      </div>
    </Modal>
  )
}
